const DEFAULT_API_BASE_URL = '/api/v1';

function trimTrailingSlash(value: string): string {
  return value.replace(/\/+$/, '');
}

/**
 * Ensure host-only bases like `http://localhost:8000` still hit `/api/v1/...`
 * (OpenAPI paths are `/api/v1/auth/refresh`, etc.). Relative `/api/v1` is
 * unchanged so the Vite proxy can keep refresh cookies same-origin.
 */
function normalizeApiBaseUrl(value: string): string {
  const trimmed = trimTrailingSlash(value.trim());
  if (!trimmed) return DEFAULT_API_BASE_URL;
  // Absolute origin with no path → append /api/v1
  if (/^https?:\/\/[^/]+$/i.test(trimmed)) {
    return `${trimmed}/api/v1`;
  }
  return trimmed;
}

/**
 * When true, the browser calls `VITE_API_BASE_URL` directly instead of the Vite
 * proxy. Only enable if the API allows CORS + credentials from your dev origin;
 * cookie refresh auth normally requires the proxy (default).
 */
function devUsesDirectRemoteApi(): boolean {
  return import.meta.env.DEV && import.meta.env.VITE_USE_DIRECT_API === 'true';
}

/**
 * Browser API origin. In DEV this is same-origin `/api/v1` so a LAN client at
 * http://<pc-ip>:5173 does not call http://localhost:8000 on the client PC.
 * Vite proxies `/api` to `VITE_API_PROXY_TARGET` or the origin of
 * `VITE_API_BASE_URL`. Network tab will still show localhost:5173 — the proxy
 * runs on the dev server.
 */
export function resolveApiBaseUrl(
  envBase?: string,
  options?: { isDev?: boolean }
): string {
  const isDev = options?.isDev ?? import.meta.env.DEV;
  if (isDev && !devUsesDirectRemoteApi()) return DEFAULT_API_BASE_URL;
  if (isDev && devUsesDirectRemoteApi()) {
    const resolved =
      envBase && envBase.trim().length > 0 ? envBase.trim() : DEFAULT_API_BASE_URL;
    return normalizeApiBaseUrl(resolved);
  }
  const resolved = envBase && envBase.trim().length > 0 ? envBase.trim() : DEFAULT_API_BASE_URL;
  return normalizeApiBaseUrl(resolved);
}

export function getApiBaseUrl(): string {
  return resolveApiBaseUrl(import.meta.env.VITE_API_BASE_URL as string | undefined);
}

export function getFloorPlansApiBase(): string {
  return getApiBaseUrl();
}

export function getImportsApiUrl(): string {
  return `${getApiBaseUrl()}/imports`;
}

export function getAuthLoginUrl(): string {
  return `${getApiBaseUrl()}/auth/login`;
}

export function getAuthRefreshUrl(): string {
  return `${getApiBaseUrl()}/auth/refresh`;
}

export function getAuthLogoutUrl(): string {
  return `${getApiBaseUrl()}/auth/logout`;
}

export function getAuthSessionsUrl(): string {
  return `${getApiBaseUrl()}/auth/sessions`;
}

export function getAuthSessionUrl(sessionId: string): string {
  return `${getApiBaseUrl()}/auth/sessions/${encodeURIComponent(sessionId)}`;
}

export function getUsersApiUrl(): string {
  return `${getApiBaseUrl()}/users`;
}

export function getAmhsRunUrl(): string {
  return `${getApiBaseUrl()}/amhs/run`;
}

export function getAmhsValidateUrl(): string {
  return `${getApiBaseUrl()}/amhs/validate`;
}

export function getAmhsStreamActiveUrl(fabId: string): string {
  const url = `${getApiBaseUrl()}/amhs/stream/active`;
  return `${url}?fabId=${encodeURIComponent(fabId)}`;
}

export function getAmhsStreamStopUrl(): string {
  return `${getApiBaseUrl()}/amhs/stream/stop`;
}

function toWebSocketUrl(value: string): string {
  if (/^https:/i.test(value)) return value.replace(/^https:/i, 'wss:');
  if (/^http:/i.test(value)) return value.replace(/^http:/i, 'ws:');
  if (/^wss?:/i.test(value)) return value;
  if (typeof window === 'undefined') return value;
  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${protocol}//${window.location.host}${value.startsWith('/') ? value : `/${value}`}`;
}

export function getAmhsStreamWebSocketUrl(): string {
  const explicit = import.meta.env.VITE_AMHS_STREAM_URL as string | undefined;
  if (explicit?.trim()) {
    const trimmed = trimTrailingSlash(explicit.trim());
    if (/\/amhs\/stream$/i.test(trimmed)) return toWebSocketUrl(trimmed);
    if (/\/api\/v1$/i.test(trimmed)) return toWebSocketUrl(`${trimmed}/amhs/stream`);
    return toWebSocketUrl(`${trimmed}/api/v1/amhs/stream`);
  }
  return toWebSocketUrl(`${getApiBaseUrl()}/amhs/stream`);
}

export function getToolboxCatalogUrl(): string {
  return `${getApiBaseUrl()}/toolbox/catalog`;
}

export function getToolboxCatalogResetUrl(): string {
  return `${getApiBaseUrl()}/toolbox/catalog/reset`;
}

export function getToolboxProductTypesUrl(): string {
  return `${getApiBaseUrl()}/toolbox/product-types`;
}

export function getToolboxToolsUrl(): string {
  return `${getApiBaseUrl()}/toolbox/tools`;
}

export function getToolboxToolUrl(toolId: string): string {
  return `${getApiBaseUrl()}/toolbox/tools/${encodeURIComponent(toolId)}`;
}
